/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, Output, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ChipSelectorZoneDropdownOptionModel} from './chip-selector-zone-dropdown-option.model';
import {StatefulComponent} from '../../../../stateful/stateful.component';
import {ChipSelectorZoneDropdownOptionStateModel} from './chip-selector-zone-dropdown-option-state.model';
import {BaseChipSelectorZoneTooltipEnum} from '../../../base-chip-selector-zone-tooltip.enum';

@Component({
    selector: 'app-chip-selector-zone-dropdown-option',
    templateUrl: './chip-selector-zone-dropdown-option.component.html',
    styleUrls: ['./chip-selector-zone-dropdown-option.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChipSelectorZoneDropdownOptionComponent
        extends StatefulComponent<ChipSelectorZoneDropdownOptionStateModel> {
    static readonly INDENT_WIDTH = 24;
    @Input()
    readonly option: ChipSelectorZoneDropdownOptionModel;

    @Input()
    readonly indentLevel = 0;

    @Output()
    readonly close = new EventEmitter<ChipSelectorZoneDropdownOptionModel>();

    @Output()
    selectedOption = new EventEmitter<ChipSelectorZoneDropdownOptionModel>();

    @ViewChild('tooltipTemplate', {static: true})
    tooltipTemplate: TemplateRef<unknown>;

    get indentMargin(): number {
        return this.indentLevel * ChipSelectorZoneDropdownOptionComponent.INDENT_WIDTH;
    };

    get arrowVisibilityCssClass(): string {
        let cssClass = '';

        if (!this.hasChildren) {
            cssClass = 'hidden';
        }

        return cssClass;
    }

    get arrowToggleCssClass(): string {
        let cssClass = '';

        if (this.state.isExpanded) {
            cssClass = 'iwp-icon-gen_arrow_down';
        } else {
            cssClass = 'iwp-icon-gen_arrow_right';
        }

        return cssClass;
    }

    get hasChildren(): boolean {
        return this.option?.children != null;
    }

    get childrenContainerClass(): string {
        let cssClass = '';

        if (!this.isShowingChildren) {
            cssClass = 'hidden';
        }

        return cssClass;
    }

    get isShowingChildren(): boolean {
        return this.hasChildren && this.state.isExpanded;
    }

    get title(): string {
        return this.option.titleTransform();
    }

    get hasDescription(): boolean {
        return this.option?.descriptionTransform != null;
    }

    get description(): string {
        return this.option.descriptionTransform();
    }

    get hasCustomTooltip(): boolean {
        return this.option?.tooltip != null;
    }

    get itemTooltip(): string | TemplateRef<unknown> {
        let tooltip: string | TemplateRef<unknown>;

        if (this.hasCustomTooltip) {
            if (this.option.tooltip.constructor === String) {
                tooltip = this.option.tooltip;
            } else {
                tooltip = this.tooltipTemplate;
            }
        } else {
            tooltip = this.title;
        }

        return tooltip;
    }

    get isTooltipDynamic(): boolean {
        return this.option?.tooltipType === BaseChipSelectorZoneTooltipEnum.DYNAMIC;
    }

    toggleParent(): void {
        if (this.hasChildren) {
            this.storeState({isExpanded: !this.state.isExpanded});
        }
    }

    closeParent(event: ChipSelectorZoneDropdownOptionModel): void {
        this.close.emit(event ?? this.option);
        this.selectedOption.emit(this.option);
        if (this.hasChildren) {
            this.storeState({isExpanded: false});
        }
    }
}
